import React, { useState, useRef, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import CircularProgressWithLabel from "../progress/CircularProgressWithLabel";
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Paper,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
//import quizData from "../../itemtypes.json";
import Markup from "../items/Markup";
import Match from "../items/Match";
import ReArrange from "../items/ReArrange";
import MCMAQuestion from "../items/MCMAQuestion";
import ApiMap from "../../utils/ApiMap";
import axios from "axios";
import AuthService from "../../utils/AuthService";
import Ranking from "../items/Ranking";
import MFB from "../items/MFB";
import Iraab from "../items/Iraab";
import { useMediaQuery } from "@mui/material";
import { parseMfbParts } from "../../utils/mfbUtils";
import GameServer from "../services/GameServer";

const getSentenceTokens = (sentence) => {
  if (typeof sentence !== "string") {
    return [];
  }

  return sentence
    .trim()
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);
};

const normalizeSentenceValue = (sentence) => {
  if (!sentence) return "";
  const tokens = getSentenceTokens(sentence)
    .map((t) => t.trim())
    .filter(Boolean);
  const isArabic = /[\u0600-\u06FF]/.test(sentence);
  // Reverse tokens if it's Arabic and visual order mismatch likely
  const normalized = isArabic ? tokens.reverse().join(" ") : tokens.join(" ");
  return normalized.normalize("NFC").trim();
};

const QuizPage = () => {
  console.log("Quizpage");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [MCMAAnswers, setMCMAAnswers] = useState({});
  const [markupAnswers, setMarkupAnswers] = useState({});
  const [matchAnswers, setMatchAnswers] = useState({});
  const [shuffledWords, setShuffledWords] = useState([]);
  const [rearrangeSelections, setRearrangeSelections] = useState({});
  const [submitted, setSubmitted] = useState({}); // Track submitted questions
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  const rationaleRef = useRef(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [matchSelections, setMatchSelections] = useState({});
  const [shuffledNames, setShuffledNames] = useState([]);
  const [shuffledValues, setShuffledValues] = useState([]);
  const [rankingSelections, setRankingSelections] = useState([]);
  const [mfbSelections, setMfbSelections] = useState({});
  const [shuffledMfbOptions, setShuffledMfbOptions] = useState([]);
  const [selectedIraabWord, setSelectedIraabWord] = useState(null);
  const [iraabAnswers, setIraabAnswers] = useState({});
  const [quizData, setQuizData] = useState(null);
  const [error, setError] = useState(null); // Error state
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [hasUserInteracted, setHasUserInteracted] = useState(true);
  const location = useLocation();
  const { chapterName } = location.state || {};
  const { gameCode, courseId, moduleId, chapterId, lessonId } = useParams();
  const [skippedQuestions, setSkippedQuestions] = useState([]);
  const [isQuizStarted, setIsQuizStarted] = useState(true);

  const [reviewedContent, setReviewedContent] = useState({});
  const attemptCode = quizData?.AttemptCode;
  const isMobile = useMediaQuery("(max-width:600px)");
  const contentId = lessonId;

  useEffect(() => {
    return () => {
      // cleanup when leaving this page
      localStorage.removeItem("quizUnsaved");
    };
  }, [location.pathname]);

  useEffect(() => {
    if (isQuizStarted && !isLoading && !quizData?.Submitted) {
      localStorage.setItem("quizUnsaved", "true");
    } else {
      localStorage.removeItem("quizUnsaved");
    }
  }, [isQuizStarted, isLoading, quizData]);

  useEffect(() => {
    if (quizData?.Submitted) {
      console.log("Quiz has already been submitted");
      setIsQuizStarted(false);
    } else {
      setIsQuizStarted(true);
    }
  }, [quizData?.Submitted]);

  useEffect(() => {
    if (gameCode && quizData?.Activities?.length > 0) {
      GameServer.notify({
        action: "msgstudentstartedgame",
        channel: gameCode,
        payload: {
          lessonId: contentId,
          ItemCount: quizData.Activities.length,
        },
      });
    }
  }, [gameCode, quizData]);

  //const gameCode = null;

  const colorMap = [
    "#ffcccb",
    "#d3ffd3",
    "#d3f4ff",
    "#f0f0f0",
    "#ffe9a1",
    "#d8b2ff",
    "#cfe2ff",
    "#c3f3d3",
    "#ffd1dc",
    "#e0c2ff",
  ];

  useEffect(() => {
    setQuizData(null);
    let url = `${ApiMap.ASSESSMENT}/course/${courseId}/module/${moduleId}/chapter/${chapterId}/assessment/${contentId}`;
    if (gameCode) {
      url = `${ApiMap.GAME_ASSESSMENT}/${gameCode}/assessment`; // ✅ multiplayer mode
    }

    const fetchData = async () => {
      try {
        const resp = await axios(url, AuthService.getAuthHeader());
        setQuizData(resp.data);
      } catch (err) {
        setError("Failed to load quiz. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [contentId, gameCode]);

  const reFetchQuiz = async () => {
    await fetchQuiz(true);
  };

  const fetchQuiz = async (retake) => {
    setIsLoading(true);
    var url = `${ApiMap.ASSESSMENT}/course/${courseId}/module/${moduleId}/chapter/${chapterId}/assessment/${contentId}`;
    if (retake) {
      url = `${url}?retake=${retake}`;
    }
    try {
      const resp = await axios(url, AuthService.getAuthHeader());
      setQuizData(resp.data);
    } catch (err) {
      setError("Failed to load lesson quiz. Please try again.");
    } finally {
      setIsLoading(false); // Stop loading after fetch completes
    }
  };

  const renderLoading = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        height: "100vh",
      }}
    >
      <CircularProgress size={24} />
    </Box>
  );

  const renderError = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    </Box>
  );

  const currentActivity = quizData?.Activities[currentQuestionIndex];
  // const isLastQuestion =
  //   currentQuestionIndex === quizData?.Activities?.length - 1;
  const options =
    currentActivity?.Type === "Markup" ? currentActivity?.Options : null;
  const types = options
    ? Object.values(options).flatMap((option) =>
        option.map((item) => item.Type)
      )
    : [];
  const typeCount = types.reduce((acc, type) => {
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  const assignColors = (types) => {
    const typeToColor = {};
    let colorIndex = 0;

    types.forEach((type) => {
      if (!typeToColor[type]) {
        // Assign the next color or loop back to the start
        typeToColor[type] = colorMap[colorIndex % colorMap.length];
        colorIndex++;
      }
    });

    return typeToColor;
  };

  // Get unique types
  const uniqueTypes = Array.from(new Set(types));

  // Assign colors to unique types
  const typeToColorMap = assignColors(uniqueTypes);

  const isButtonActive =
    currentActivity?.Type === "Markup"
      ? Object.keys(markupAnswers).length > 0
      : currentActivity?.Type === "Match"
      ? matchAnswers[currentActivity?.ContentId] &&
        Object.keys(matchAnswers[currentActivity?.ContentId]).length > 0
      : currentActivity?.Type === "ReArrange" ||
        currentActivity?.Type === "Ranking"
      ? hasUserInteracted
      : currentActivity?.Type === "MFB"
      ? mfbSelections[currentActivity?.ContentId] &&
        Object.values(mfbSelections[currentActivity?.ContentId]).every(
          (val) => val !== ""
        )
      : currentActivity?.Type === "Iraab"
      ? Object.keys(iraabAnswers).length > 0
      : currentActivity?.Type === "MCMA"
      ? (() => {
          const answer = MCMAAnswers[currentActivity?.ContentId];
          return Array.isArray(answer)
            ? answer.length > 0 // multiple choice case
            : answer !== undefined; // single choice case
        })()
      : false;

  const sanitizeTitle = (title) => {
    if (!title) return "";

    // Remove `autoplay` from <audio> tags
    return title.replace(
      /<audio([^>]*)autoplay([^>]*)>/g,
      "<audio$1$2>" // Keep the audio tag but without autoplay
    );
  };

  useEffect(() => {
    if (submitted[currentActivity?.ContentId] && rationaleRef.current) {
      setTimeout(() => {
        if (rationaleRef.current) {
          // Double check before scrolling
          rationaleRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    }
  }, [submitted, currentActivity?.ContentId]);

  useEffect(() => {
    const totalQuestions = quizData?.Activities?.length;
    const progress = (correctAnswers / totalQuestions) * 100;
    setProgressValue(progress); // Update progress after correctAnswers changes
  }, [correctAnswers]); // This runs whenever correctAnswers changes

  useEffect(() => {
    if (currentActivity?.Options && currentActivity?.Type === "Match") {
      // Shuffle names and values when a new activity is loaded
      const shuffledNames = shuffleArray(
        currentActivity.Options.map((option) => option.Name)
      );
      const shuffledValues = shuffleArray(
        currentActivity.Options.map((option) => option.Value)
      );

      setShuffledNames(shuffledNames);
      setShuffledValues(shuffledValues);
    }
  }, [currentActivity?.Options]); // Dependency ensures it runs only when Options change

  useEffect(() => {
    if (currentActivity?.Options) {
      if (currentActivity.Type === "Ranking") {
        const shuffledRanking = shuffleArray(
          currentActivity.Options.map((option, index) => ({
            ...option,
            Id: option.Id ?? `temp-${index}`, // Ensure Id exists
          }))
        );
        setRankingSelections(shuffledRanking);
      } else if (currentActivity.Type === "ReArrange") {
        const optionsList = Array.isArray(currentActivity.Options)
          ? currentActivity.Options
          : [];
        const baseOption = optionsList.find(
          (option) => typeof option?.Value === "string" && option.Value.trim()
        );
        const baseTokens = getSentenceTokens(baseOption?.Value);
        const shuffledRearrangeWords = shuffleArray([...baseTokens]);
        setShuffledWords(shuffledRearrangeWords);
      } else if (currentActivity.Type === "MFB") {
        const value = currentActivity.Options?.[0]?.Value || "";
        const extractedOptions = parseMfbParts(value).map((part) =>
          Array.isArray(part) ? shuffleArray([...part]) : part
        );

        setShuffledMfbOptions(extractedOptions);
      }
    }
  }, [currentActivity]);

  const handleSkip = (contentId) => {
    setSkippedQuestions((prev) => {
      // add to skipped if not already there
      const updatedSkipped = prev.includes(contentId)
        ? prev
        : [...prev, contentId];

      let nextIndex = currentQuestionIndex + 1;
      const totalQuestions = quizData?.Activities?.length;

      // Find the next unanswered question (ignore submitted only, allow already skipped)
      while (
        nextIndex < totalQuestions &&
        submitted[quizData.Activities[nextIndex].ContentId]
      ) {
        nextIndex++;
      }

      if (nextIndex < totalQuestions) {
        setCurrentQuestionIndex(nextIndex);
      } else {
        // If no unanswered left, go back to first skipped but unsubmitted
        const nextSkippedIndex = quizData?.Activities?.findIndex(
          (q) => updatedSkipped.includes(q.ContentId) && !submitted[q.ContentId]
        );

        if (nextSkippedIndex !== -1) {
          setCurrentQuestionIndex(nextSkippedIndex);
        }
      }

      return updatedSkipped;
    });
  };

  const hasSkippedOrUnsubmitted = quizData?.Activities?.some(
    (act) =>
      !submitted[act.ContentId] && act.ContentId !== currentActivity?.ContentId
  );

  const handleNext = () => {
    const totalQuestions = quizData?.Activities?.length;

    let nextIndex = currentQuestionIndex + 1;

    // First, check if there are any skipped questions that should be answered next
    const skippedIndex = quizData?.Activities?.findIndex(
      (activity) =>
        skippedQuestions.includes(activity.ContentId) &&
        !submitted[activity.ContentId]
    );

    if (skippedIndex !== -1) {
      // If there are skipped questions, move to the first skipped one
      setCurrentQuestionIndex(skippedIndex);
    } else {
      // If no skipped question left, move to the next unanswered one
      while (
        nextIndex < totalQuestions &&
        (submitted[quizData.Activities[nextIndex].ContentId] ||
          skippedQuestions.includes(quizData.Activities[nextIndex].ContentId))
      ) {
        nextIndex++; // Skip over already answered or skipped questions
      }

      if (nextIndex < totalQuestions) {
        // Move to the next unanswered question
        setCurrentQuestionIndex(nextIndex);
      } else {
        // If there are no more unanswered questions, show results
        setShowResults(true);
      }
    }
  };

  const sendUserResp = async (aid, attemptCode, correctCount, startTime) => {
    const userAnswer = {
      crsId: courseId,
      modId: moduleId,
      chpId: chapterId,
      lsnId: contentId,
      attemptCode: attemptCode,
      responseTimeInMillsec: Date.now() - startTime,
      answeredCorrect: correctCount === 1,
      percentCompleted: progressValue,
      gameCode: gameCode || null, // ✅ multiplayer mode
    };

    await axios.post(
      `${ApiMap.ASSESSMENT}/item/${aid}/attempt`,
      userAnswer,
      AuthService.getAuthHeader()
    );

    if (gameCode) {
      console.log("🎯 Sending msganswersubmitted via GameServer");
      GameServer.notify(
        gameCode, // ✅ channel
        "msganswersubmitted", // ✅ event
        { ...userAnswer, aid }, // ✅ payload
        {} // ✅ handlers (optional)
      );
    }
  };

  const completeQuiz = async () => {
    try {
      let assessment = {
        crsId: String(courseId),
        modId: String(moduleId),
        chpId: String(chapterId),
        lsnId: String(contentId),
        attemptCode: quizData?.AttemptCode,
        gameCode: gameCode || null,
      };

      await axios.post(
        `${ApiMap.ASSESSMENT}/submitassessment`,
        assessment,
        AuthService.getAuthHeader()
      );

      if (gameCode) {
        GameServer.notify({
          action: "studentcompleted",
          channel: gameCode,
          payload: assessment,
        });
      }

      console.log("Assessment submitted ✅");
    } catch (err) {
      console.error("Error submitting assessment", err);
    }
  };

  const handleOptionChange = (activityId, optionId, selectedValue) => {
    const activity = quizData?.Activities.find(
      (q) => q.ContentId === activityId
    );
    if (!activity) return;

    if (activity.Type === "MCMA") {
      const correctAnswersCount = (activity.Options || []).filter(
        (option) => option.IsCorrect
      ).length;

      setMCMAAnswers((prev) => {
        if (correctAnswersCount === 1) {
          // Radio - single choice
          return {
            ...prev,
            [activityId]: optionId, // store a single value
          };
        } else {
          // Checkbox - multiple choice
          const existing = prev[activityId] || [];
          if (existing.includes(optionId)) {
            return {
              ...prev,
              [activityId]: existing.filter((id) => id !== optionId),
            };
          } else {
            return {
              ...prev,
              [activityId]: [...existing, optionId],
            };
          }
        }
      });
    } else if (activity.Type === "Match") {
      setMatchAnswers((prev) => ({
        ...prev,
        [activityId]: {
          ...prev[activityId],
          [optionId]: selectedValue,
        },
      }));
    } else if (activity.Type === "ReArrange") {
      const normalizedSelection = normalizeSentenceValue(selectedValue);
      setRearrangeSelections((prev) => ({
        ...prev,
        [activityId]: normalizedSelection,
      }));
    } else if (activity.Type === "Ranking") {
      setRankingSelections((prev) => ({
        ...prev,
        [activityId]: selectedValue, // selectedValue should be the new ranked order
      }));
    } else if (activity.Type === "MFB") {
      setMfbSelections((prev) => ({
        ...prev,
        [activityId]: {
          ...prev[activityId],
          [optionId]: selectedValue, // Stores selected value for each dropdown
        },
      }));
    } else if (activity.Type === "Iraab") {
      setIraabAnswers((prev) => ({
        ...prev,
        [activityId]: {
          ...prev[activityId],
          [optionId]: selectedValue, // Stores selected haraka for each word
        },
      }));
    }
  };

  const handleSelectChange = (optionId, selectedValue) => {
    const updatedSelections = {
      ...matchSelections,
      [optionId]: selectedValue,
    };
    setMatchSelections(updatedSelections);

    // Notify parent of the change
    handleOptionChange(currentActivity?.ContentId, optionId, selectedValue);
  };

  const handleSubmit = (activityId) => {
    const startTime = Date.now();
    const gameCode = "";
    const activity = quizData.Activities.find(
      (act) => act.ContentId === activityId
    );
    if (!activity) return;

    let correctCount = 0;

    // ===== Evaluate correctness by type =====
    if (activity.Type === "MCMA") {
      const selected = MCMAAnswers[activityId];
      const selectedAnswers = Array.isArray(selected)
        ? selected
        : selected !== undefined
        ? [selected]
        : [];

      const correctAnswers = activity.Options.filter(
        (opt) => opt.IsCorrect
      ).map((opt) => opt.id);

      const correctSelections = selectedAnswers.filter((selected) =>
        correctAnswers.includes(selected)
      ).length;

      correctCount = correctSelections / correctAnswers.length;
    }

    if (activity.Type === "Markup") {
      let correctSelections = 0;
      Object.keys(activity.Options).forEach((optionKey) => {
        const index = Number(optionKey) - 1;
        const correctTypes = activity.Options[optionKey].map(
          (item) => item.Type
        );
        const userSelections = markupAnswers[index] || [];
        const userSelectedTypes = userSelections.map((entry) => entry.type);

        if (
          userSelectedTypes.length === correctTypes.length &&
          JSON.stringify(userSelectedTypes.slice().sort()) ===
            JSON.stringify(correctTypes.slice().sort())
        ) {
          correctSelections += 1;
        }
      });
      if (correctSelections === Object.keys(activity.Options).length) {
        correctCount = 1;
      }
    }

    if (activity.Type === "Match") {
      const matchSelections = matchAnswers[activityId];
      const isCorrect = activity.Options.every((option) =>
        Object.values(matchSelections).includes(option.Value)
      );
      if (isCorrect) correctCount = 1;
    }

    if (activity.Type === "ReArrange") {
      const userSentence = normalizeSentenceValue(
        rearrangeSelections[activity.ContentId]
      );

      const correctSentences = Array.isArray(activity.Options)
        ? activity.Options.map((option) =>
            normalizeSentenceValue(option?.Value)
          )
        : [];

      console.log("User:", userSentence);
      console.log("Correct:", correctSentences);

      if (
        userSentence &&
        correctSentences.some(
          (sentence) => sentence && sentence === userSentence
        )
      ) {
        correctCount = 1;
      }
    }

    if (activity.Type === "Ranking") {
      const userOrder = rankingSelections.map((opt) => opt.Value);
      const correctOrder = activity.Options.map((opt) => opt.Value);
      const isCorrect =
        JSON.stringify(userOrder) === JSON.stringify(correctOrder);
      if (isCorrect) correctCount = 1;
    }

    if (activity.Type === "MFB") {
      const userSelectionsObj = mfbSelections[activityId] || {};
      const userSelections = Object.values(userSelectionsObj).map((word) =>
        word.trim()
      );
      const correctAnswers = activity.Options[0].Value.split(" ")
        .map((chunk) => (chunk.includes("/") ? chunk.split("/")[0] : null))
        .filter(Boolean)
        .map((word) => word.trim());

      const isCorrect =
        userSelections.length === correctAnswers.length &&
        userSelections.every((word, i) => word === correctAnswers[i]);
      if (isCorrect) correctCount = 1;
    }

    if (activity.Type === "Iraab") {
      const correctWords = activity.Iraab.Value.split(" ");
      let allCorrect = true;
      correctWords.forEach((correctWord, index) => {
        const harakaatPattern = /[ًٌٍَُِْ]$/;
        const userWord = iraabAnswers[index] || "";
        if (!harakaatPattern.test(correctWord)) return;
        if (userWord.trim() !== correctWord.trim()) {
          allCorrect = false;
        }
      });

      if (allCorrect) correctCount = 1;
    }

    // ===== Update submission state =====
    const newSubmitted = {
      ...submitted,
      [activityId]: {
        isSubmitted: true,
        isCorrect: correctCount >= 1,
      },
    };

    setSubmitted(newSubmitted);

    console.log(correctCount);

    if (correctCount > 0) {
      setCorrectAnswers((prev) => prev + correctCount);
    }

    sendUserResp(
      activity.ContentId,
      quizData.AttemptCode,
      correctCount,
      startTime,
      gameCode
    );

    const allActivities = quizData?.Activities || [];

    // find if there are any unanswered (not skipped, not submitted yet)
    const unanswered = allActivities.filter(
      (act) =>
        !newSubmitted[act.ContentId]?.isSubmitted &&
        !skippedQuestions.includes(act.ContentId)
    );

    // find skipped but unsubmitted
    const unsubmittedSkipped = skippedQuestions.filter(
      (id) => !newSubmitted[id]?.isSubmitted
    );

    const allSubmitted = allActivities.every(
      (activity) => newSubmitted[activity.ContentId]?.isSubmitted
    );

    if (allSubmitted) {
      setIsQuizStarted(false);

      if (unanswered.length > 0) {
        // 👉 still unanswered left (non-skipped), go to the first one
        const firstUnansweredIndex = allActivities.findIndex((q) =>
          unanswered.some((u) => u.ContentId === q.ContentId)
        );
        if (firstUnansweredIndex !== -1) {
          setCurrentQuestionIndex(firstUnansweredIndex);
          return;
        }
      } else if (unsubmittedSkipped.length > 0) {
        // 👉 if all normal answered, now revisit skipped
        const firstSkippedIndex = allActivities.findIndex((q) =>
          unsubmittedSkipped.includes(q.ContentId)
        );
        if (firstSkippedIndex !== -1) {
          setCurrentQuestionIndex(firstSkippedIndex);
          return;
        }
      }

      // after skipped are also done → correctness flow
      const incorrectActivities = allActivities.filter(
        (activity) => !newSubmitted[activity.ContentId]?.isCorrect
      );

      if (incorrectActivities.length === 0) {
        setShowResults(true);
        completeQuiz();
      } else {
        const updatedSubmitted = { ...newSubmitted };
        incorrectActivities.forEach((act) => {
          if (act.ContentId !== activityId) {
            delete updatedSubmitted[act.ContentId];
          }
        });
        setSubmitted(updatedSubmitted);

        const firstIncorrectIndex = allActivities.findIndex((a) =>
          incorrectActivities.some((inc) => inc.ContentId === a.ContentId)
        );
        if (firstIncorrectIndex !== -1) {
          setCurrentQuestionIndex(firstIncorrectIndex);
        }
      }
    }
  };

  const handleRetake = (activityId) => {
    // Remove the submission status
    setSubmitted((prev) => {
      const updated = { ...prev };
      delete updated[activityId];
      return updated;
    });

    // Reset MCMA answers if applicable
    setMCMAAnswers((prev) => {
      const updated = { ...prev };
      delete updated[activityId];
      return updated;
    });

    setReviewedContent((prev) => {
      const updated = { ...prev };
      delete updated[contentId];
      return updated;
    });
  };

  const handleRetry = (activityId) => {
    // Remove the submission status
    setSubmitted((prev) => {
      const updated = { ...prev };
      delete updated[activityId];
      return updated;
    });
  };

  const handleTryAgain = () => {
    setMCMAAnswers({});
    setSubmitted({});
    setCorrectAnswers(0);
    setProgressValue(0);
    setCurrentQuestionIndex(0);
    setShowResults(false);
    setMarkupAnswers({});
    setMatchSelections({});
    setHasUserInteracted(false);
    setMatchAnswers({});
  };

  const handleRankingDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;
    // Ensure we create a new array reference
    const updatedRanking = [...rankingSelections];
    const [moved] = updatedRanking.splice(source.index, 1);
    updatedRanking.splice(destination.index, 0, moved);
    // Update state correctly
    setRankingSelections([...updatedRanking]);
    setHasUserInteracted(true);
  };

  const renderIraabActivity = () => {
    if (!currentActivity?.Iraab?.Value) return null;
    const harakaatOptions = ["َ", "ُ", "ِ", "ْ"]; // Fatha, Damma, Kasra, Sukun

    const words = currentActivity.Iraab.Value.split(" ").map((word) => {
      const harakaatPattern = /[ًٌٍَُِْ]/; // Arabic diacritics
      const lastChar = word.slice(-1);
      const hasHaraka = harakaatPattern.test(lastChar);
      const cleanedWord = hasHaraka ? word.slice(0, -1) : word;

      return { word: cleanedWord, hasHaraka };
    });

    const handleWordClick = (index, hasHaraka) => {
      if (!hasHaraka) return;
      setSelectedIraabWord(selectedIraabWord === index ? null : index); // Toggle selection
    };

    const handleIraabSelection = (haraka) => {
      if (selectedIraabWord !== null) {
        setIraabAnswers((prev) => ({
          ...prev,
          [selectedIraabWord]: `${words[selectedIraabWord].word}${haraka}`,
        }));
      }
      setSelectedIraabWord(null);
    };

    return (
      <Box
        style={{
          direction: "rtl",
          textAlign: "right",
        }}
      >
        {words.map(({ word, hasHaraka }, index) => (
          <Box
            key={index}
            display="inline-block"
            textAlign="center"
            m={0.5}
            position="relative"
          >
            <Typography
              onClick={() => handleWordClick(index, hasHaraka)}
              style={{
                backgroundColor: hasHaraka ? "#fff" : "#f0f0f0",
                cursor: hasHaraka ? "pointer" : "default",
                opacity: hasHaraka ? 1 : 0.6,
                fontFamily: "Traditional Arabic, serif",
              }}
            >
              {iraabAnswers[index] || word}
            </Typography>
          </Box>
        ))}

        {/* Centered Harakaat Dropdown Overlay */}
        {selectedIraabWord !== null && (
          <Box
            position="fixed"
            top={0}
            left={0}
            width="100vw"
            height="100vh"
            bgcolor="rgba(0,0,0,0.4)"
            zIndex={1300}
            display="flex"
            alignItems="center"
            justifyContent="center"
            onClick={() => setSelectedIraabWord(null)} // clicking outside closes it
          >
            <Box
              bgcolor="white"
              borderRadius="8px"
              p={2}
              boxShadow={5}
              onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
            >
              <Stack spacing={1}>
                {harakaatOptions.map((haraka, idx) => (
                  <Typography
                    key={idx}
                    onClick={() => handleIraabSelection(haraka)}
                    sx={{
                      display: "inline-block", // Makes it behave like an inline element
                      backgroundColor: "#1976d2", // Optional: background color for each haraka
                      padding: "8px 16px", // Adjust padding for spacing
                      borderRadius: "4px", // Rounded corners for the text box
                      cursor: "pointer", // Makes it clickable
                      color: "#ffffff", // Text color
                      fontWeight: "bold", // Optional: make text bold
                      fontSize: "1.2rem", // Optional: Adjust font size
                      minWidth: "80px", // Same minWidth as Button
                      textAlign: "center", // Center the text
                      fontFamily: "Traditional Arabic, serif",
                    }}
                  >
                    {words[selectedIraabWord].word + haraka}
                  </Typography>
                ))}
              </Stack>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderMFBActivity = () => {
    if (!shuffledMfbOptions.length) return null;
    return (
      <Box
        dir="rtl"
        style={{
          textAlign: "right",
          fontSize: "1.5rem",
          fontFamily: "Traditional Arabic, serif",
        }}
      >
        <Box
          dir="rtl"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1, // space between select boxes
            textAlign: "right",
            fontSize: "1.5rem",
          }}
        >
          {shuffledMfbOptions.map((part, index) => (
            <React.Fragment key={index}>
              {Array.isArray(part) ? (
                <Select
                  value={
                    mfbSelections[currentActivity?.ContentId]?.[index] || ""
                  }
                  displayEmpty
                  sx={{
                    minWidth: 120,
                    fontSize: "1.5rem",
                    "& .MuiSelect-select": {
                      p: "2px", // 🔹 remove default padding around text
                    },
                  }}
                  onChange={(e) =>
                    handleOptionChange(
                      currentActivity?.ContentId,
                      index,
                      e.target.value
                    )
                  }
                >
                  {part.map((option, i) => (
                    <MenuItem
                      key={i}
                      value={option}
                      sx={{ fontSize: "1.5rem" }}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Box
                  component="span"
                  sx={{ fontSize: "1.5rem", whiteSpace: "pre-wrap" }}
                >
                  {part}
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    );
  };

  const renderRankingActivity = () => {
    const isSubmitted = submitted[currentActivity.ContentId] || false;

    return (
      <Box sx={{ padding: 2 }}>
        <DragDropContext onDragEnd={handleRankingDragEnd}>
          <Droppable
            droppableId="rankingActivity"
            direction="vertical"
            isDropDisabled={isSubmitted}
          >
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  padding: 2,
                  border: "1px solid #ccc",
                  borderRadius: 2,
                  backgroundColor: "#f5f5f5",
                }}
              >
                {rankingSelections.map((option, index) => (
                  <Draggable
                    key={String(option.Id)}
                    draggableId={String(option.Id)}
                    index={index}
                    isDragDisabled={isSubmitted}
                  >
                    {(provided) => (
                      <Paper
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          padding: 1,
                          backgroundColor: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: 2,
                          borderRadius: 1,
                          cursor: "move",
                        }}
                      >
                        <Typography variant="body1">{option.Value}</Typography>
                      </Paper>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    );
  };

  const renderReArrangeActivity = () => {
    const isSubmitted = submitted[currentActivity.ContentId] || false;
    const isRtlDirection =
      typeof currentActivity?.Direction === "string" &&
      currentActivity.Direction.toUpperCase() === "RTL";
    // Handle rearranged words after drag and drop
    const handleOnDragEnd = (result) => {
      const { source, destination } = result;
      if (!destination || source.index === destination.index) return;
      const updatedWords = Array.from(shuffledWords);
      const [removed] = updatedWords.splice(source.index, 1);
      updatedWords.splice(destination.index, 0, removed);
      setShuffledWords(updatedWords); // Update shuffled words state
      setHasUserInteracted(true);
      handleOptionChange(currentActivity.ContentId, 1, updatedWords.join(" ")); // Update the answer
    };

    return (
      <Box sx={{ padding: 2 }}>
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable
            droppableId="rearrangeActivity"
            direction={isMobile ? "vertical" : "horizontal"}
            isDropDisabled={isSubmitted}
          >
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                dir={isRtlDirection ? "rtl" : "ltr"}
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: 2,
                  padding: 2,
                  border: "1px solid #ccc",
                  borderRadius: 2,
                  backgroundColor: "#f5f5f5",
                  flexWrap: isMobile ? "nowrap" : "wrap",
                  justifyContent: isRtlDirection ? "flex-end" : "flex-start",
                  textAlign: isRtlDirection ? "right" : "left",
                  alignItems: isMobile
                    ? isRtlDirection
                      ? "flex-end"
                      : "flex-start"
                    : "stretch",
                }}
              >
                {shuffledWords.map((word, index) => (
                  <Draggable
                    key={index}
                    draggableId={String(index)}
                    index={index}
                    isDragDisabled={isSubmitted}
                  >
                    {(provided, snapshot) => (
                      <Paper
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          padding: 1,
                          backgroundColor: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: snapshot.isDragging ? 6 : 2,
                          borderRadius: 1,
                          cursor: "move",
                          fontSize: "1.2rem",
                          width: isMobile ? "100%" : "auto", // full width on mobile
                          textAlign: "center",
                          direction: isRtlDirection ? "rtl" : "ltr",
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: "1.5rem",
                            fontFamily: "Traditional Arabic, serif",
                            direction: isRtlDirection ? "rtl" : "ltr",
                            textAlign: isRtlDirection ? "right" : "left",
                          }}
                        >
                          {word}
                        </Typography>
                      </Paper>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    );
  };

  const renderMarkupActivity = () => {
    const words = currentActivity?.Text.split(" ")
      .map((word) => word.trim()) // Trim spaces from each word
      .filter((word) => word !== ""); // Remove empty strings

    const handleWordClick = (wordIndex) => {
      if (!selectedColor || submitted[currentActivity?.ContentId]) return;

      // Find the associated type for the selected color
      const selectedType = Object.entries(typeToColorMap).find(
        ([type, color]) => color === selectedColor
      )?.[0];

      if (!selectedType) {
        return;
      }

      setMarkupAnswers((prevMarkupAnswers) => {
        const existingSelections = prevMarkupAnswers[wordIndex] || [];
        // Check if this color is already selected for the word
        const existingIndex = existingSelections.findIndex(
          (entry) => entry.color === selectedColor
        );

        if (existingIndex !== -1) {
          // Remove the existing selection
          const updatedSelections = existingSelections.filter(
            (_, index) => index !== existingIndex
          );
          return updatedSelections.length > 0
            ? { ...prevMarkupAnswers, [wordIndex]: updatedSelections }
            : Object.fromEntries(
                Object.entries(prevMarkupAnswers).filter(
                  ([key]) => parseInt(key, 10) !== wordIndex
                )
              );
        }
        const updatedMarkupAnswers = {
          ...prevMarkupAnswers,
          [wordIndex]: [
            ...existingSelections,
            { color: selectedColor, type: selectedType },
          ],
        };
        return updatedMarkupAnswers;
      });
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "5px",
          alignItems: "center",
          marginBottom: "20px",
          direction: currentActivity?.Direction === "RTL" ? "rtl" : "ltr", // Set text direction
          fontFamily: "Traditional Arabic, serif",
        }}
      >
        {words.map((word, index) => {
          const wordSelections = markupAnswers[index] || []; // Get stored colors for this word
          const backgroundStyle =
            wordSelections.length > 1
              ? `linear-gradient(to right, ${wordSelections
                  .map((entry) => entry.color)
                  .join(", ")})`
              : wordSelections.length === 1
              ? wordSelections[0].color // Single color
              : "transparent";

          return (
            <Typography
              key={index}
              component="span"
              onClick={() => handleWordClick(index)}
              sx={{
                cursor: submitted[currentActivity?.ContentId]
                  ? "not-allowed"
                  : "pointer",
                padding: "4px",
                fontSize: currentActivity?.style?.fontSize || "1.5rem",
                background: backgroundStyle,
                opacity: submitted[currentActivity?.ContentId] ? 0.5 : 1,
                fontFamily: "Traditional Arabic, serif",
              }}
            >
              {word}
            </Typography>
          );
        })}
      </Box>
    );
  };

  const shuffleArray = (array) => {
    return array.sort(() => Math.random() - 0.5); // Simple shuffle function
  };

  if (isLoading) return renderLoading(); // Show loading spinner
  if (error) return renderError(); // Show error message

  return (
    <Box sx={{ maxWidth: 950, margin: "0 auto" }}>
      <Card
        sx={{
          padding: 2,
          maxWidth: 850,
          margin: "0 auto",
        }}
      >
        <Box>
          {currentActivity?.ContentId && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.75rem",
                fontStyle: "italic",
                color: "text.secondary",
              }}
            >
              ({currentActivity?.ContentId})
            </Typography>
          )}
        </Box>
        {quizData?.Activities?.length !== 0 ? (
          !showResults ? (
            <>
              <CardContent sx={{ padding: 3 }}>
                {/* Options displayed above the question for Markup type only */}
                {currentActivity?.Type === "Markup" && (
                  <Box
                    sx={{
                      marginBottom: "20px",
                      display: "flex",
                      flexWrap: "wrap", // Allows buttons to wrap on smaller screens
                      gap: 2,
                      justifyContent: "center", // Center items when wrapped
                    }}
                  >
                    {Object.entries(typeCount).map(([type, count]) => {
                      const isSelected = selectedColor === typeToColorMap[type];

                      return (
                        <Button
                          key={type}
                          sx={{
                            backgroundColor: typeToColorMap[type] || "default",
                            color: "#000",
                            border: isSelected ? "2px solid #000" : "none", // <-- underline if selected

                            "&:hover": {
                              backgroundColor:
                                typeToColorMap[type] || "default",
                            },
                            minWidth: "100px",
                            flex: "1 1 auto",
                          }}
                          onClick={() =>
                            !submitted[currentActivity?.ContentId] &&
                            setSelectedColor(typeToColorMap[type])
                          }
                          disabled={submitted[currentActivity?.ContentId]}
                        >
                          {`${count} ${type}`}
                        </Button>
                      );
                    })}
                  </Box>
                )}

                <Box sx={{ marginBottom: "20px" }}>
                  <Typography
                    variant="casting"
                    gutterBottom
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.75rem",
                      fontStyle: "italic",
                    }}
                  >
                    Question
                  </Typography>

                  <Typography
                    variant="body1"
                    gutterBottom
                    color="primary.main"
                    fontWeight="bold"
                    fontFamily="Merriweather, serif"
                  >
                    {currentQuestionIndex + 1}/{quizData?.Activities?.length}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    fontSize: "0.95rem",
                    "& *": {
                      fontSize: "0.95rem !important",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeTitle(currentActivity?.Title),
                  }}
                />

                {currentActivity?.Type === "MCMA" && (
                  <MCMAQuestion
                    currentActivity={currentActivity}
                    submitted={submitted[currentActivity?.ContentId]}
                    handleOptionChange={handleOptionChange}
                    answers={MCMAAnswers}
                    rationaleRef={rationaleRef}
                    reviewedContent={reviewedContent}
                    setReviewedContent={setReviewedContent}
                  />
                )}
                {currentActivity?.Type === "Match" && (
                  <Match
                    currentActivity={currentActivity}
                    submitted={submitted[currentActivity?.ContentId]}
                    shuffledNames={shuffledNames}
                    handleSelectChange={handleSelectChange}
                    shuffledValues={shuffledValues}
                    matchSelections={matchSelections}
                  />
                )}
                {currentActivity?.Type === "ReArrange" &&
                  renderReArrangeActivity()}
                {currentActivity?.Type === "Ranking" && renderRankingActivity()}
                {currentActivity?.Type === "Markup" && renderMarkupActivity()}
                {currentActivity?.Type === "MFB" && renderMFBActivity()}
                {currentActivity?.Type === "Iraab" && renderIraabActivity()}
                {submitted[currentActivity?.ContentId] &&
                  currentActivity?.Type !== "MCMA" &&
                  currentActivity?.Type !== "Match" && (
                    <Box
                      ref={rationaleRef}
                      sx={{
                        marginTop: 2,
                        borderRadius: "8px",
                        display: "flex",
                        flexDirection: "column",
                        // backgroundColor:
                        //   currentActivity?.Type === "Markup"
                        //     ? "#e8f5e9" // Adjust based on Markup logic
                        //     : currentActivity?.Options?.find(
                        //         (opt) =>
                        //           opt.id ===
                        //           Number(
                        //             markupAnswers[currentActivity?.ContentId]
                        //           )
                        //       )?.IsCorrect
                        //     ? "#e8f5e9"
                        //     : "#ffebee",
                        backgroundColor:
                          currentActivity?.Type === "Markup"
                            ? markupAnswers[currentActivity?.ContentId] &&
                              Object.entries(
                                markupAnswers[currentActivity?.ContentId]
                              ).every(
                                ([wordIndex, selectedColors]) =>
                                  Array.isArray(selectedColors) &&
                                  selectedColors.every((color) =>
                                    Object.values(
                                      currentActivity?.Options || {}
                                    ).some(
                                      (optionsArray) =>
                                        Array.isArray(optionsArray) &&
                                        optionsArray.some(
                                          (opt) => opt.Type === color
                                        )
                                    )
                                  )
                              )
                              ? "#e8f5e9"
                              : "#ffebee"
                            : currentActivity?.Type === "MFB"
                            ? (() => {
                                const userSelections = Object.values(
                                  mfbSelections?.[currentActivity.ContentId] ||
                                    {}
                                );

                                const parts = parseMfbParts(
                                  currentActivity?.Options?.[0]?.Value || ""
                                );
                                const correctAnswers = parts
                                  .filter(
                                    (part) => Array.isArray(part) && part.length
                                  )
                                  .map((options) => options[0].trim());

                                const sanitizedSelections = userSelections.map(
                                  (v) => (typeof v === "string" ? v.trim() : v)
                                );

                                const isCorrect =
                                  sanitizedSelections.length ===
                                    correctAnswers.length &&
                                  sanitizedSelections.every(
                                    (selection, i) =>
                                      selection === correctAnswers[i]
                                  );

                                return isCorrect ? "#e8f5e9" : "#ffebee";
                              })()
                            : currentActivity?.Type === "ReArrange"
                            ? (() => {
                                const userSentence = normalizeSentenceValue(
                                  rearrangeSelections?.[
                                    currentActivity?.ContentId
                                  ]
                                );

                                const correctSentences = Array.isArray(
                                  currentActivity?.Options
                                )
                                  ? currentActivity.Options.map((option) =>
                                      normalizeSentenceValue(option?.Value)
                                    )
                                  : [];

                                // Allow both normal and reversed match for Arabic
                                const reversedUserSentence = userSentence
                                  ?.split(" ")
                                  ?.reverse()
                                  ?.join(" ");

                                const isCorrect = correctSentences.some(
                                  (s) =>
                                    s === userSentence ||
                                    s === reversedUserSentence
                                );

                                return isCorrect ? "#e8f5e9" : "#ffebee";
                              })()
                            : currentActivity?.Options?.find(
                                (opt) =>
                                  opt.id ===
                                  Number(
                                    markupAnswers[currentActivity?.ContentId]
                                  )
                              )?.IsCorrect
                            ? "#e8f5e9"
                            : "#ffebee",
                      }}
                    >
                      {submitted[currentActivity?.ContentId] && (
                        <Box
                          ref={rationaleRef}
                          sx={{
                            marginTop: 2,
                            padding: 1,
                            borderRadius: "8px",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {currentActivity?.Type === "Markup" && (
                            <Markup
                              currentActivity={currentActivity}
                              markupAnswers={markupAnswers}
                              typeToColorMap={typeToColorMap}
                            />
                          )}

                          {/* {currentActivity?.Type === "MCMA" && (
                        <MCMAQuestion
                          currentActivity={currentActivity}
                          answers={answers}
                        />
                      )} */}
                          {/* {currentActivity?.Type === "Match" && (
                          <Match
                            currentActivity={currentActivity}
                            matchSelections={matchSelections}
                          />
                        )} */}

                          {currentActivity?.Type === "ReArrange" && (
                            <ReArrange
                              currentActivity={currentActivity}
                              rearrangeSelections={rearrangeSelections}
                            />
                          )}
                          {currentActivity?.Type === "Ranking" && (
                            <Ranking
                              currentActivity={currentActivity}
                              rankingSelections={rankingSelections}
                            />
                          )}
                          {currentActivity?.Type === "MFB" && (
                            <MFB
                              currentActivity={currentActivity}
                              mfbSelections={mfbSelections}
                            />
                          )}
                          {currentActivity?.Type === "Iraab" && (
                            <Iraab
                              currentActivity={currentActivity}
                              iraabAnswers={iraabAnswers}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  )}

                {submitted[currentActivity?.ContentId] && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    {quizData?.Activities.every(
                      (activity) => submitted[activity.ContentId]?.isSubmitted
                    ) ? (
                      // ✅ All questions submitted
                      Object.values(submitted).every((s) => s.isCorrect) ? (
                        // All correct → Finish
                        <Button
                          onClick={handleNext}
                          variant="contained"
                          color="primary"
                          sx={{
                            height: 30,
                            padding: "16px 42px",
                            fontSize: "0.75rem",
                            borderRadius: "15px",
                          }}
                        >
                          Finish
                        </Button>
                      ) : (
                        // Some incorrect/skipped → Try Again
                        <Button
                          onClick={() =>
                            handleRetake(currentActivity?.ContentId)
                          }
                          variant="contained"
                          color="secondary"
                          sx={{
                            height: 30,
                            padding: "16px 24px",
                            fontSize: "0.75rem",
                            borderRadius: "15px",
                          }}
                        >
                          Try Again
                        </Button>
                      )
                    ) : (
                      // ❌ Not all submitted yet → Next
                      <Button
                        onClick={handleNext}
                        variant="contained"
                        color="primary"
                        sx={{
                          height: 30,
                          padding: "16px 42px",
                          fontSize: "0.75rem",
                          borderRadius: "15px",
                        }}
                      >
                        Next
                      </Button>
                    )}

                    {submitted[currentActivity?.ContentId]?.isSubmitted &&
                      !submitted[currentActivity?.ContentId]?.isCorrect &&
                      reviewedContent?.[currentActivity?.ContentId] && (
                        <Button
                          onClick={() =>
                            handleRetake(currentActivity?.ContentId)
                          }
                          variant="contained"
                          color="secondary"
                          sx={{
                            height: 30,
                            padding: "16px 24px",
                            fontSize: "0.75rem",
                            borderRadius: "15px",
                          }}
                        >
                          Retake
                        </Button>
                      )}
                  </Box>
                )}
              </CardContent>

              <CardActions sx={{ justifyContent: "center" }}>
                {!submitted[currentActivity?.ContentId] &&
                  quizData?.Activities?.length > 1 &&
                  hasSkippedOrUnsubmitted && (
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{
                        height: 30,
                        fontSize: "0.75rem",
                        padding: "16px 42px",
                        borderRadius: "15px",
                      }}
                      onClick={() => handleSkip(currentActivity?.ContentId)}
                    >
                      Skip
                    </Button>
                  )}

                {!submitted[currentActivity?.ContentId] && (
                  <Button
                    onClick={() => handleSubmit(currentActivity?.ContentId)}
                    variant="contained"
                    color="primary"
                    disabled={!isButtonActive}
                    sx={{
                      height: 30,
                      fontSize: "0.75rem",
                      padding: "16px 42px",
                      borderRadius: "15px",
                    }}
                  >
                    Submit
                  </Button>
                )}
              </CardActions>
            </>
          ) : (
            quizData?.Activities?.every(
              (activity) =>
                submitted[activity.ContentId] ||
                skippedQuestions.includes(activity.ContentId)
            ) && (
              <Box sx={{ textAlign: "center", mt: 3 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  sx={{ marginBottom: 2 }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      textAlign: "left",
                      fontWeight: 600,
                      color: "secondary.main",
                    }}
                  >
                    Chapter : {chapterName}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      textAlign: "left",
                      fontWeight: 600,
                    }}
                  >
                    Quiz Results
                  </Typography>
                </Box>

                {/* Circular Progress Bar */}
                <CircularProgressWithLabel
                  progressStep={10}
                  maxValue={100}
                  value={progressValue}
                  handleTryAgain={handleTryAgain}
                />
              </Box>
            )
          )
        ) : (
          <Box style={{ margin: "75px 25px 25px 25px" }}>
            <Typography sx={{ mb: "10px" }}>
              You have completed this assessment before.
            </Typography>
            <Button
              sx={{ background: "primary.main" }}
              variant="contained"
              color="primary"
              onClick={reFetchQuiz}
            >
              RETAKE TEST
            </Button>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default QuizPage;
